// components/form/useFormErrorFocus.ts
import type {
	FieldError,
	FieldErrors,
	FieldValues,
	Path,
	UseFormSetFocus,
} from 'react-hook-form'
import { toast } from 'react-toastify'
import i18n from '@/lib/i18n'

export function useFormErrorFocus<T extends FieldValues>(
	errors: FieldErrors<T>,
	setFocus: UseFormSetFocus<T>,
) {
	const handleError = (submitErrors?: FieldErrors<T>) => {
		const currentErrors = submitErrors ?? errors
		const firstError = Object.keys(currentErrors)[0] as Path<T> | undefined

		if (!firstError) return

		const fieldError = currentErrors[firstError] as FieldError | undefined
		const refValue = fieldError?.ref as unknown

		const elementFromRef =
			refValue instanceof HTMLElement
				? refValue
				: refValue instanceof Element
					? (refValue as HTMLElement)
					: null

		// scroll DOM (prefere o ref do próprio erro e usa name como fallback)
		const element =
			elementFromRef ??
			(document.querySelector(
				`[name="${String(firstError)}"]`,
			) as HTMLElement | null)

		const containerByFieldName = document.querySelector(
			`[data-slot="field-content"][data-field-name="${String(firstError)}"]`,
		) as HTMLElement | null

		const focusableFromContainer = containerByFieldName?.querySelector(
			'input, select, textarea, button, [tabindex]:not([tabindex="-1"])',
		) as HTMLElement | null

		if (!element && !containerByFieldName) {
			setFocus(firstError)
			return
		}

		const container =
			containerByFieldName ??
			(element?.closest('[data-slot="field-content"]') as HTMLElement | null) ??
			element

		if (!container) {
			setFocus(firstError)
			return
		}

		if (typeof container.scrollIntoView === 'function') {
			container.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
				inline: 'nearest',
			})
		}

		container.setAttribute('data-error-focused', 'true')

		// Aguarda um frame para não brigar com o scroll automático do foco
		requestAnimationFrame(() => {
			setFocus(firstError)

			if (!elementFromRef && focusableFromContainer) {
				focusableFromContainer.focus()
			}
		})

		setTimeout(() => {
			container.removeAttribute('data-error-focused')
		}, 2000)

		// 🔔 TOAST (🔥 novo)
		const message =
			typeof fieldError?.message === 'string'
				? i18n.t('inspections.yellowCard.validationError')
				: i18n.t('inspections.yellowCard.validationError')

		toast.error(message, {
			toastId: 'form-error', // evita duplicação
		})
	}

	return { handleError }
}
